import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OnlineCourseService } from '@lx/quote-tool/lite/online/services';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { DialogBasedSelector } from '@lx/quote-tool/lite/shared/ui/directives';
import { OnlineCoursePredefinedSelection, OnlineCourseSelectorComponent } from '@lx/quote-tool/lite/online/ui';
import { SelectorFieldContainerComponent, SidePanelComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { SchoolLocation } from '@lx/quote-tool/lite/shared/types';
import { PredefinedOnlineSelectionsWithCourses } from '@lx/shared/data-access/types';

@Component({
  standalone: true,
  selector: 'lx-online-course-selector-field',
  templateUrl: './online-course-selector-field.component.html',
  styleUrls: ['./online-course-selector-field.component.scss'],
  imports: [SidePanelComponent, SelectorFieldContainerComponent, OnlineCourseSelectorComponent],
})
export class OnlineCourseSelectorFieldComponent extends DialogBasedSelector<CourseDetails> implements OnChanges {
  @Input() selectedSchoolLocation: SchoolLocation | undefined;
  @Input() currentDuration: number | undefined;
  @Input() currentStartDate: string | undefined;

  @Output() predefinedSelectionsChanged = new EventEmitter<OnlineCoursePredefinedSelection>();

  protected readonly courseOptions = signal<PredefinedOnlineSelectionsWithCourses[] | undefined>(undefined);

  private readonly onlineCourseService = inject(OnlineCourseService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedSchoolLocation'] && this.selectedSchoolLocation?.schoolCode) {
      this.loadCourseOptions();
    }
  }

  handlePredefinedSelections(predefinedSelections: OnlineCoursePredefinedSelection): void {
    this.predefinedSelectionsChanged.emit(predefinedSelections);
  }

  /**
   * Loads and merges course data when school location changes
   * @private
   */
  private loadCourseOptions(): void {
    if (!this.selectedSchoolLocation?.schoolCode) {
      this.courseOptions.set(undefined);
      return;
    }

    this.onlineCourseService
      .getCourseOptions(this.selectedSchoolLocation.schoolCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((courseOptions) => {
        this.courseOptions.set(courseOptions);
      });
  }
}
